/**
 * Output layer preview: renders playState layers inside LiveKitRoom.
 * Mirrors what the player outputs (Stage, Room, Live, Remote composition).
 * Requires LiveKit connection for livekitTrack* layers; static/composite layers use PreviewStaticLayerContent.
 */

import '@livekit/components-styles';
import {
  LiveKitRoom,
  ParticipantContext,
  useConnectionState,
  useTracks,
  VideoTrack,
  AudioTrack,
} from '@livekit/components-react';
import type { TrackReference } from '@livekit/components-react';
import { ConnectionState, RemoteTrackPublication, Track } from 'livekit-client';
import { VideocamOffOutlined } from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { buildCroppedMap, selectByCroppedPreference } from '@/helpers/cropped.helper';
import { collectOutputLayerTrackSids } from '@/helpers/resolve-output-preview-layers';
import { showFirstOfSpeakerGroups } from '@/helpers/speaker-group.helper';
import { useHasSharedLiveKitRoom } from './shared-livekit-room.context';
import {
  filterSubscriberAudioTracks,
  filterSubscriberVisualTracks,
  findVisualTrackByPublicationSid,
  findVisualTrackForPublisherPlayerId,
  publisherPlayerIdFromLayerItemId,
} from '@/helpers/preview/track-filtering.helper';
import { getBackgroundStyles } from '@/helpers/preview/background.helper';
import { usePreviewStageMetrics } from '@/helpers/preview/preview-viewport-scale.helper';
import { PreviewStaticLayerContent } from '@/components/preview/preview-static-layer-renderer';
import type { PreviewLayer } from '@/components/preview/preview-static-layer-renderer';
import { htmlFontResolvesAgainstStage } from '@/helpers/preview/font-size-cqh.helper';
import { getLayerCutClipPath } from '@/helpers/preview/layer-cut.helper';
import type { PlayerEventSettings } from '@/types/preview.types';
import { isPreviewAudioCapableLayer, PreviewAudioToggleButton } from '@/components/preview/preview-audio-toggle-button';
import { AgentDeckLiveSlideOverlay } from '@/components/preview/agent-deck-live-slide-overlay';
import {
  StillFrameReadyProvider,
  useStillFrameReadyRegistration,
} from '@/components/preview/still-frame-ready-context';
import { buildStillFrameLayersResetKey } from '@/helpers/build-still-frame-layers-reset-key.helper';
import { resolveLiveKitTrackHold } from '@/helpers/preview/livekit-track-hold.helper';
import { isSyntheticRoomCamPlaceholder, roomCamTileIsUnavailable } from '@/helpers/preview/room-cam-tile.helper';
import { useTranslations } from '@/i18n/use-translations';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';

export interface OutputLayer {
  layerId: number;
  componentId: string;
  position: { x: number; y: number; w: number; h: number };
  /** Horizontal crop (percent of layer width) applied via clip-path, mirroring the player. */
  cutLeft?: number | null;
  cutRight?: number | null;
  data: Record<string, string>;
  style?: { textColor?: string; fontSizeMultiplier?: number };
  visible?: boolean;
  preload?: boolean;
  /** Legacy single track; many layers (e.g. media agent) use `trackSids` only. */
  trackSid?: string;
  /** Room / template may send multiple publications (e.g. deck + cropped); match any for video. */
  trackSids?: string[];
  itemId?: string;
  audible?: boolean;
}

import type { PresentationSlideNavigationScope } from '@/components/preview/preview-presentation-slides-layer.types';

const PREVIEW_VISUAL_TRACK_SOURCES = [
  { source: Track.Source.Camera, withPlaceholder: false as const },
  { source: Track.Source.ScreenShare, withPlaceholder: false as const },
  { source: Track.Source.Unknown, withPlaceholder: false as const },
];

/** Still-frame readiness must not block Control Play for the full publication hold. */
const STILL_FRAME_LIVEKIT_SETTLE_TIMEOUT_MS = 2500;

export interface OutputLayerPreviewProps {
  /** Omitted when an ancestor `SharedLiveKitRoomProvider` owns the connection. */
  serverUrl?: string;
  token?: string;
  layers: OutputLayer[];
  settings?: PlayerEventSettings | null;
  onError?: (error: Error) => void;
  height?: string | number;
  slideNavigationScope?: PresentationSlideNavigationScope;
  /** Next look-ahead: step stills locally instead of waiting for the presentation computer. */
  preferAbsoluteSlideGoto?: boolean;
  sequencePlaybackControls?: boolean;
  /** Agent-deck slide chrome renders inside the preview frame (above layers, below audio). */
  showAgentDeckSlideOverlay?: boolean;
  liveContextState?: LiveContextStateDto;
  /** Live entity ref for Current slide-chrome suppress (advance hide). */
  entityRef?: string;
  /** Freeze non-LiveKit video file layers on the first frame (Control Now/Next). */
  stillFrame?: boolean;
  /** Live Current: file videos play at the players' position (layer `playbackStartedAt`). */
  syncFileVideo?: boolean;
  onStillFrameReadyChange?: (ready: boolean) => void;
  /** True once the shared or owned room reaches Connected; stays true across reconnects. */
  onRoomConnectedChange?: (connected: boolean) => void;
  /** See SimulationOutputPreview.stillFrameResetNonce. */
  stillFrameResetNonce?: number;
  /** Controlled preview listen state (On Air / Simulation). Ignored when stillFrame. */
  previewAudioEnabled?: boolean;
  onPreviewAudioEnabledChange?: (enabled: boolean) => void;
  /**
   * Show the in-frame bottom-right listen toggle.
   * Default false when stillFrame; otherwise true unless a parent renders the control elsewhere.
   */
  showInFrameAudioToggle?: boolean;
}

export function OutputLayerPreview({
  serverUrl,
  token,
  layers,
  settings,
  onError,
  height = '100%',
  slideNavigationScope,
  preferAbsoluteSlideGoto = false,
  sequencePlaybackControls,
  showAgentDeckSlideOverlay = false,
  liveContextState,
  entityRef,
  stillFrame = false,
  syncFileVideo = false,
  onStillFrameReadyChange,
  onRoomConnectedChange,
  stillFrameResetNonce = 0,
  previewAudioEnabled,
  onPreviewAudioEnabledChange,
  showInFrameAudioToggle,
}: OutputLayerPreviewProps) {
  const stillFrameResetKey = useMemo(
    () => buildStillFrameLayersResetKey(layers, stillFrameResetNonce),
    [layers, stillFrameResetNonce],
  );

  const hasSharedRoom = useHasSharedLiveKitRoom();

  const content = (
    <OutputLayerContent
      layers={layers}
      settings={settings}
      slideNavigationScope={slideNavigationScope}
      sequencePlaybackControls={sequencePlaybackControls}
      preferAbsoluteSlideGoto={preferAbsoluteSlideGoto}
      showAgentDeckSlideOverlay={showAgentDeckSlideOverlay}
      liveContextState={liveContextState}
      entityRef={entityRef}
      stillFrame={stillFrame}
      syncFileVideo={syncFileVideo}
      previewAudioEnabled={previewAudioEnabled}
      onPreviewAudioEnabledChange={onPreviewAudioEnabledChange}
      showInFrameAudioToggle={showInFrameAudioToggle}
    />
  );

  const framed = stillFrame ? (
    <StillFrameReadyProvider resetKey={stillFrameResetKey} onReadyChange={onStillFrameReadyChange}>
      {content}
    </StillFrameReadyProvider>
  ) : (
    content
  );
  const body = onRoomConnectedChange ? (
    <>
      <RoomConnectedReporter onConnectedChange={onRoomConnectedChange} />
      {framed}
    </>
  ) : (
    framed
  );

  const canConnectOwnRoom = Boolean(token && serverUrl);

  return (
    <Box sx={{ height, width: '100%', position: 'relative' }}>
      {canConnectOwnRoom && !hasSharedRoom ? (
        <LiveKitRoom
          video={false}
          audio={false}
          connect={true}
          token={token!}
          serverUrl={serverUrl!}
          onError={onError}
          style={{ height: '100%', width: '100%' }}
        >
          {body}
        </LiveKitRoom>
      ) : hasSharedRoom ? (
        body
      ) : (
        // useTracks throws outside RoomContext — do not mount layer hooks until a room exists.
        <Box sx={{ height: '100%', width: '100%', bgcolor: '#000' }} />
      )}
    </Box>
  );
}

/** Latched so a reconnect does not flip Control Current back to not-ready. */
function RoomConnectedReporter({ onConnectedChange }: { onConnectedChange: (connected: boolean) => void }) {
  const connected = useConnectionState() === ConnectionState.Connected;
  const latchedRef = useRef(false);
  const onChangeRef = useRef(onConnectedChange);
  useEffect(() => {
    onChangeRef.current = onConnectedChange;
  }, [onConnectedChange]);

  useEffect(() => {
    if (connected) {
      latchedRef.current = true;
    }
    onChangeRef.current(latchedRef.current);
  }, [connected]);

  return null;
}

const LIVEKIT_COMPONENT_IDS = new Set([
  'livekitTrackScreenshare',
  'livekitTrackMediaAgent',
  'livekitTrackRoomCam',
  'livekitTrackRemoteSpeaker',
]);

/**
 * Program mute must not silence Control FE monitor listen for stage/remote mics.
 * Include grid componentIds (rare in built layers; grids expand to singles) and itemId
 * prefixes so the fallback path stays aligned with authorizedAudioTrackSids.
 */
function bypassesProgramMuteForPreviewAudio(layer: Pick<OutputLayer, 'componentId' | 'itemId'>): boolean {
  return (
    layer.componentId === 'livekitTrackRoomCam' ||
    layer.componentId === 'livekitTrackRoomCamGrid' ||
    layer.componentId === 'livekitTrackRemoteSpeaker' ||
    layer.componentId === 'livekitTrackRemoteSpeakerGrid' ||
    Boolean(layer.itemId?.startsWith('roomCam:')) ||
    Boolean(layer.itemId?.startsWith('remoteSpeaker:'))
  );
}

function OutputLayerContent({
  layers,
  settings,
  slideNavigationScope,
  sequencePlaybackControls,
  preferAbsoluteSlideGoto = false,
  showAgentDeckSlideOverlay,
  liveContextState,
  entityRef,
  stillFrame = false,
  syncFileVideo = false,
  previewAudioEnabled: previewAudioEnabledProp,
  onPreviewAudioEnabledChange,
  showInFrameAudioToggle,
}: {
  layers: OutputLayer[];
  settings?: PlayerEventSettings | null;
  slideNavigationScope?: PresentationSlideNavigationScope;
  sequencePlaybackControls?: boolean;
  preferAbsoluteSlideGoto?: boolean;
  showAgentDeckSlideOverlay?: boolean;
  liveContextState?: LiveContextStateDto;
  entityRef?: string;
  stillFrame?: boolean;
  syncFileVideo?: boolean;
  previewAudioEnabled?: boolean;
  onPreviewAudioEnabledChange?: (enabled: boolean) => void;
  showInFrameAudioToggle?: boolean;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const { scale: stageViewportScale, stageHeightPx } = usePreviewStageMetrics(frameRef);
  const [internalAudioEnabled, setInternalAudioEnabled] = useState(false);
  const audioEnabled = stillFrame ? false : (previewAudioEnabledProp ?? internalAudioEnabled);
  const setAudioEnabled = (enabled: boolean) => {
    if (stillFrame) return;
    onPreviewAudioEnabledChange?.(enabled);
    if (previewAudioEnabledProp === undefined) {
      setInternalAudioEnabled(enabled);
    }
  };
  const authorizedTrackSids = useMemo(
    () => [...new Set(layers.flatMap((l) => collectOutputLayerTrackSids(l)))],
    [layers],
  );
  /**
   * Program mute (`layer.audible`) must not silence Control FE monitor listen for stage/remote mics.
   * Other LiveKit audio still follows program audible.
   */
  const authorizedAudioTrackSids = useMemo(
    () => [
      ...new Set(
        layers
          .filter((l) => l.audible !== false || bypassesProgramMuteForPreviewAudio(l))
          .flatMap((l) => collectOutputLayerTrackSids(l)),
      ),
    ],
    [layers],
  );

  const sortedLayers = useMemo(
    () => showFirstOfSpeakerGroups([...layers].sort((a, b) => a.layerId - b.layerId)),
    [layers],
  );

  const allVisualTracks = useTracks(PREVIEW_VISUAL_TRACK_SOURCES, { onlySubscribed: false });
  const allAudioTracks = useTracks(
    [
      { source: Track.Source.Microphone, withPlaceholder: false },
      { source: Track.Source.ScreenShareAudio, withPlaceholder: false },
      { source: Track.Source.Unknown, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );

  const audioTracks = useMemo((): TrackReference[] => {
    const explicit = filterSubscriberAudioTracks(allAudioTracks, authorizedAudioTrackSids);
    if (explicit.length > 0 || authorizedAudioTrackSids.length > 0) {
      return explicit;
    }
    const layerAllowsAudio = layers.some(
      (l) =>
        (LIVEKIT_COMPONENT_IDS.has(l.componentId) || bypassesProgramMuteForPreviewAudio(l)) &&
        (l.audible !== false || bypassesProgramMuteForPreviewAudio(l)),
    );
    if (!layerAllowsAudio || authorizedTrackSids.length === 0) {
      return [];
    }
    const videoParticipantIdentities = new Set(
      filterSubscriberVisualTracks(allVisualTracks, authorizedTrackSids).map((t) => t.participant.identity),
    );
    return allAudioTracks.filter(
      (t): t is TrackReference =>
        t.publication?.trackSid !== undefined &&
        !t.participant.isLocal &&
        t.publication.kind === Track.Kind.Audio &&
        videoParticipantIdentities.has(t.participant.identity),
    );
  }, [allAudioTracks, allVisualTracks, authorizedAudioTrackSids, authorizedTrackSids, layers]);

  const hasLiveKitLayers = useMemo(
    () => sortedLayers.some((layer) => layer.visible !== false && LIVEKIT_COMPONENT_IDS.has(layer.componentId)),
    [sortedLayers],
  );
  const agentDeckVideoOnAir = sortedLayers.some(
    (layer) =>
      layer.componentId === 'livekitTrackMediaAgent' &&
      layer.visible !== false &&
      typeof layer.itemId === 'string' &&
      layer.itemId.startsWith('mediaAgent:'),
  );

  const canPlayPreviewAudio = useMemo(
    () =>
      !stillFrame &&
      (audioTracks.length > 0 ||
        hasLiveKitLayers ||
        sortedLayers.some((layer) => layer.visible !== false && isPreviewAudioCapableLayer(layer))),
    [audioTracks.length, hasLiveKitLayers, sortedLayers, stillFrame],
  );
  const showAudioToggle = canPlayPreviewAudio && (showInFrameAudioToggle ?? true);

  return (
    <Box
      ref={frameRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        containerType: 'size',
        ...(settings ? getBackgroundStyles(settings) : { bgcolor: '#000' }),
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          bgcolor: '#000',
          pointerEvents: 'none',
        }}
      />
      {audioTracks.map((trackRef) => (
        <AudioTrack
          key={`${trackRef.participant.identity}-${trackRef.publication?.trackSid}`}
          trackRef={trackRef}
          muted={!audioEnabled}
          volume={audioEnabled ? 1 : 0}
        />
      ))}
      {sortedLayers.map((layer) => {
        const visible = layer.visible !== false;
        const isPreload = layer.preload === true;
        const shouldRender = visible || isPreload;
        const shouldShow = visible && !isPreload;

        if (!shouldRender || !layer.position) return null;

        const cutClipPath = getLayerCutClipPath(layer.cutLeft, layer.cutRight);
        const containerSx = {
          position: 'absolute' as const,
          left: `${layer.position.x}%`,
          top: `${layer.position.y}%`,
          width: `${layer.position.w}%`,
          height: `${layer.position.h}%`,
          overflow: 'hidden' as const,
          ...(cutClipPath ? { clipPath: cutClipPath, WebkitClipPath: cutClipPath } : {}),
          visibility: shouldShow ? ('visible' as const) : ('hidden' as const),
          opacity: shouldShow ? 1 : 0,
          pointerEvents: shouldShow ? ('auto' as const) : ('none' as const),
          zIndex: layer.layerId,
          // Match player: HTML scales with the layer box. Captions and info overlays use the stage.
          ...(htmlFontResolvesAgainstStage(layer) ? {} : { containerType: 'size' as const }),
        };

        const key = `layer-${layer.layerId}`;

        if (LIVEKIT_COMPONENT_IDS.has(layer.componentId)) {
          return (
            <Box key={key} sx={containerSx}>
              <LiveKitTrackLayer layer={layer} authorizedTrackSids={authorizedTrackSids} stillFrame={stillFrame} />
            </Box>
          );
        }

        return (
          <Box key={key} sx={containerSx}>
            <StaticLayerPlaceholder
              layer={layer}
              settings={settings}
              previewAudioEnabled={audioEnabled}
              stageViewportScale={stageViewportScale}
              stageHeightPx={stageHeightPx}
              slideNavigationScope={slideNavigationScope}
              sequencePlaybackControls={stillFrame ? false : sequencePlaybackControls}
              preferAbsoluteSlideGoto={preferAbsoluteSlideGoto || !agentDeckVideoOnAir}
              stillFrame={stillFrame}
              syncFileVideo={syncFileVideo}
              listenForSlideKeys={
                shouldShow &&
                !(
                  agentDeckVideoOnAir &&
                  (layer.componentId === 'imageSequence' || layer.componentId === 'presentationSlides')
                )
              }
              onAir={shouldShow}
            />
          </Box>
        );
      })}
      {showAgentDeckSlideOverlay ? (
        <AgentDeckLiveSlideOverlay layers={layers} liveContextState={liveContextState} entityRef={entityRef} />
      ) : null}
      {showAudioToggle && (
        <PreviewAudioToggleButton audioEnabled={audioEnabled} onToggle={() => setAudioEnabled(!audioEnabled)} />
      )}
    </Box>
  );
}

function LiveKitTrackLayer({
  layer,
  authorizedTrackSids,
  stillFrame = false,
}: {
  layer: OutputLayer;
  authorizedTrackSids: string[];
  stillFrame?: boolean;
}) {
  const { componentId } = layer;
  const t = useTranslations();
  const candidateSids = useMemo(() => collectOutputLayerTrackSids(layer), [layer]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const roomConnected = useConnectionState() === ConnectionState.Connected;
  const syntheticRoomCam = isSyntheticRoomCamPlaceholder(layer.itemId);
  // A speaker slot with no on-stage speaker stays empty, even when look-ahead lent it a track: any
  // camera here would be a stand-in. The players leave it out, so it is settled and blank here too.
  const emptySpeakerSlot = componentId === 'livekitTrackRemoteSpeaker' && !layer.itemId?.startsWith('remoteSpeaker:');
  const [publicationWaitElapsed, setPublicationWaitElapsed] = useState(false);
  const settleReady = useStillFrameReadyRegistration(stillFrame);
  const readySentRef = useRef(false);

  const allTracks = useTracks(PREVIEW_VISUAL_TRACK_SOURCES, { onlySubscribed: false });

  const authorizedVisualTracks = useMemo(
    () => filterSubscriberVisualTracks(allTracks, authorizedTrackSids),
    [allTracks, authorizedTrackSids],
  );

  const croppedMap = useMemo(() => buildCroppedMap(authorizedVisualTracks), [authorizedVisualTracks]);
  const deduppedAuthorized = useMemo(
    () => selectByCroppedPreference(authorizedVisualTracks, croppedMap, false),
    [authorizedVisualTracks, croppedMap],
  );

  const trackToShow = useMemo((): TrackReference | undefined => {
    const requiresExactTrackSid = componentId === 'livekitTrackMediaAgent';
    const publisherPlayerId = publisherPlayerIdFromLayerItemId(layer.itemId);

    if (emptySpeakerSlot) {
      return undefined;
    }

    if (candidateSids.length > 0) {
      for (const sid of candidateSids) {
        const exact = deduppedAuthorized.find((t) => t.publication?.trackSid === sid);
        if (exact) return exact;
      }
      if (requiresExactTrackSid) {
        for (const sid of candidateSids) {
          if (!authorizedTrackSids.includes(sid)) continue;
          const byPublication = findVisualTrackByPublicationSid(allTracks, sid);
          if (byPublication) return byPublication;
        }
        if (publisherPlayerId) {
          return findVisualTrackForPublisherPlayerId(allTracks, publisherPlayerId, componentId);
        }
        return undefined;
      }
    } else if (requiresExactTrackSid) {
      if (publisherPlayerId) {
        return findVisualTrackForPublisherPlayerId(allTracks, publisherPlayerId, componentId);
      }
      return undefined;
    }

    if (componentId === 'livekitTrackRoomCam' || componentId === 'livekitTrackRemoteSpeaker') {
      const fromPublisher = publisherPlayerId
        ? findVisualTrackForPublisherPlayerId(allTracks, publisherPlayerId, componentId)
        : undefined;
      return fromPublisher ?? deduppedAuthorized.find((t) => t.source === Track.Source.Camera);
    }
    if (componentId === 'livekitTrackScreenshare') {
      const fromPublisher = publisherPlayerId
        ? findVisualTrackForPublisherPlayerId(allTracks, publisherPlayerId, componentId)
        : undefined;
      return (
        fromPublisher ?? deduppedAuthorized.find((t) => t.source === Track.Source.ScreenShare) ?? deduppedAuthorized[0]
      );
    }
    if (publisherPlayerId) {
      return findVisualTrackForPublisherPlayerId(allTracks, publisherPlayerId, componentId);
    }
    return deduppedAuthorized[0];
  }, [allTracks, authorizedTrackSids, candidateSids, componentId, deduppedAuthorized, emptySpeakerSlot, layer.itemId]);

  const instantSid = trackToShow?.publication?.trackSid ?? null;
  const [heldBackup, setHeldBackup] = useState<TrackReference | undefined>(undefined);
  const heldBackupRef = useRef(heldBackup);
  useLayoutEffect(() => {
    heldBackupRef.current = heldBackup;
  });

  /* eslint-disable react-hooks/set-state-in-effect -- hold last good publication briefly during churn */
  useEffect(() => {
    if (emptySpeakerSlot) {
      setHeldBackup(undefined);
      return;
    }
    const publisherPlayerId = publisherPlayerIdFromLayerItemId(layer.itemId);
    const decision = resolveLiveKitTrackHold({
      hasInstantTrack: Boolean(trackToShow && instantSid),
      layerVisible: layer.visible !== false,
      heldSid: heldBackupRef.current?.publication?.trackSid ?? null,
      candidateSids,
      authorizedTrackSids,
      componentId,
      publisherPlayerId,
    });

    if (decision.action === 'keep' && trackToShow) {
      setHeldBackup((prev) => (prev?.publication?.trackSid === trackToShow.publication?.trackSid ? prev : trackToShow));
      return;
    }
    if (decision.action === 'clear') {
      setHeldBackup(undefined);
      return;
    }

    if (decision.action !== 'schedule') {
      return;
    }

    const timer = window.setTimeout(() => {
      setHeldBackup(undefined);
    }, decision.holdMs);
    return () => clearTimeout(timer);
  }, [
    authorizedTrackSids,
    candidateSids,
    componentId,
    emptySpeakerSlot,
    instantSid,
    layer.itemId,
    layer.visible,
    trackToShow,
  ]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const displayTrack = emptySpeakerSlot ? undefined : (trackToShow ?? heldBackup);
  const displaySid = displayTrack?.publication?.trackSid ?? null;
  const cameraUnavailable = roomCamTileIsUnavailable({
    componentId,
    itemId: layer.itemId,
    hasDisplayTrack: Boolean(displaySid),
    roomConnected,
    publicationWaitElapsed,
  });

  /* eslint-disable react-hooks/set-state-in-effect -- publication wait is a timer, not derived render state */
  useEffect(() => {
    if (displaySid || syntheticRoomCam || emptySpeakerSlot || !roomConnected) {
      setPublicationWaitElapsed(false);
      return;
    }
    setPublicationWaitElapsed(false);
    const timer = window.setTimeout(() => setPublicationWaitElapsed(true), STILL_FRAME_LIVEKIT_SETTLE_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [displaySid, emptySpeakerSlot, layer.itemId, layer.layerId, roomConnected, syntheticRoomCam]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const candidateSidsKey = candidateSids.join(',');
  useEffect(() => {
    readySentRef.current = false;
  }, [layer.layerId, layer.itemId, candidateSidsKey]);

  useEffect(() => {
    if (!settleReady) {
      return;
    }
    const signalReady = () => {
      if (readySentRef.current) {
        return;
      }
      readySentRef.current = true;
      settleReady();
    };

    // Next cannot attach a second agent deck while one is on air. Current waits for the live track.
    if (stillFrame && componentId === 'livekitTrackMediaAgent') {
      signalReady();
      return;
    }

    if (displaySid || cameraUnavailable || emptySpeakerSlot) {
      signalReady();
      return;
    }

    if (!stillFrame) {
      return;
    }

    // Don't block Control spinner forever if a publication never arrives.
    const timer = window.setTimeout(signalReady, STILL_FRAME_LIVEKIT_SETTLE_TIMEOUT_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [cameraUnavailable, componentId, displaySid, emptySpeakerSlot, settleReady, stillFrame]);

  useLayoutEffect(() => {
    const pub = displayTrack?.publication;
    if (!pub || !(pub instanceof RemoteTrackPublication)) return;
    if (!pub.isSubscribed) {
      pub.setSubscribed(true);
    }
  }, [displayTrack]);

  useLayoutEffect(() => {
    if (!displaySid) return;
    const el = videoRef.current;
    if (!el) return;
    const kick = () => {
      void el.play().catch(() => {});
    };
    kick();
    el.addEventListener('loadeddata', kick);
    el.addEventListener('canplay', kick);
    return () => {
      el.removeEventListener('loadeddata', kick);
      el.removeEventListener('canplay', kick);
    };
  }, [displaySid]);

  const boxSx = {
    width: '100%',
    height: '100%',
    overflow: 'hidden' as const,
    backgroundColor: '#000',
  };

  if (emptySpeakerSlot) {
    return <Box sx={{ width: '100%', height: '100%' }} />;
  }

  if (displayTrack) {
    return (
      <Box sx={boxSx}>
        <ParticipantContext.Provider value={displayTrack.participant}>
          <VideoTrack
            ref={videoRef}
            trackRef={displayTrack}
            manageSubscription={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              backgroundColor: '#000',
            }}
          />
        </ParticipantContext.Provider>
      </Box>
    );
  }

  if (cameraUnavailable) {
    const unavailableLabel = t(
      'flows.detail.control.play.cameraTrackUnavailable',
      undefined,
      'Camera track unavailable',
    );
    return (
      <Box
        sx={{
          ...boxSx,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          px: 0.5,
        }}
        role="img"
        aria-label={unavailableLabel}
      >
        <VideocamOffOutlined sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 'clamp(1.25rem, 28%, 2.5rem)' }} />
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.7)',
            textAlign: 'center',
            lineHeight: 1.15,
            fontSize: 'clamp(0.55rem, 8%, 0.75rem)',
          }}
        >
          {unavailableLabel}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={boxSx}>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255,255,255,0.5)',
        }}
      >
        <CircularProgress size={28} aria-label="Loading preview" sx={{ color: 'rgba(255,255,255,0.8)' }} />
      </Box>
    </Box>
  );
}

function StaticLayerPlaceholder({
  layer,
  settings,
  previewAudioEnabled,
  stageViewportScale = 1,
  stageHeightPx = 0,
  slideNavigationScope,
  sequencePlaybackControls,
  preferAbsoluteSlideGoto = false,
  stillFrame = false,
  syncFileVideo = false,
  listenForSlideKeys = true,
  onAir = false,
}: {
  layer: OutputLayer;
  settings?: PlayerEventSettings | null;
  previewAudioEnabled?: boolean;
  stageViewportScale?: number;
  stageHeightPx?: number;
  slideNavigationScope?: PresentationSlideNavigationScope;
  preferAbsoluteSlideGoto?: boolean;
  sequencePlaybackControls?: boolean;
  stillFrame?: boolean;
  syncFileVideo?: boolean;
  listenForSlideKeys?: boolean;
  /** Visible and not preloading. Live stills then play embedded clips instead of leaving the poster. */
  onAir?: boolean;
}) {
  return (
    <PreviewStaticLayerContent
      layer={layer as PreviewLayer}
      settings={settings}
      previewAudioEnabled={stillFrame ? false : previewAudioEnabled}
      videoControls={!stillFrame && !syncFileVideo}
      stillFrame={stillFrame}
      syncFileVideo={syncFileVideo}
      stageViewportScale={stageViewportScale}
      stageHeightPx={stageHeightPx}
      slideNavigationScope={slideNavigationScope}
      sequencePlaybackControls={sequencePlaybackControls}
      preferAbsoluteSlideGoto={preferAbsoluteSlideGoto}
      listenForSlideKeys={listenForSlideKeys}
      onAir={onAir}
    />
  );
}
